import { csvText } from './csv.ts';
import { dealLabel, parseDeal, type Deal } from './deals.ts';
import { describeRule, parseDeliveryRules, readDeliveryRules, type DeliveryRule } from './delivery.ts';
import { formatMoney } from './money.ts';
import { parsePlace, type Place } from './places.ts';
import {
  effectiveStock, findVariant, LEGACY_UNLIMITED_QUANTITY, legacyImageVariantLinks, legacyVariantStock, optionPhotoMap, parseVariantRows, readOptionPhotos, readVariantRows,
  rowHasStock, rowsFromChoiceStock, variantGroups, variantLabel, variantPrice, type VariantRow,
} from './variants.ts';

// Listings (SPEC sections 2 and 7): four types, custom option groups,
// variants with their own stock, price and photo (variants.ts), and delivery
// styles. They are stored in
// the existing `products` collection with the legacy field names, so the live
// app keeps reading them; v2-only fields are added alongside.

export const LISTING_TYPES = ['item', 'food', 'service', 'big', 'rent', 'stay'] as const;
export type ListingType = (typeof LISTING_TYPES)[number];
export const LISTING_TYPE_LABELS: Record<ListingType, string> = { item: 'Item', food: 'Food', service: 'Service', big: 'Big-ticket', rent: 'For rent', stay: 'Short stay' };
// Big-ticket and long-term rent are enquire-only: buyers message the shop,
// and checkout refuses them (DECISIONS, 22 October).
export const ENQUIRE_ONLY: ReadonlySet<ListingType> = new Set(['big', 'rent']);
export const PROPERTY_TYPES: ReadonlySet<ListingType> = new Set(['rent', 'stay']);

// zones: named areas the buyer picks (the live app's model); rules: delivery
// areas on the map, priced from the buyer's pin (delivery.ts).
export const DELIVERY_STYLES = ['zones', 'rules', 'flat', 'free', 'pickup_only'] as const;
export type DeliveryStyle = (typeof DELIVERY_STYLES)[number];

export const MAX_PHOTOS = 6;
export const MAX_OPTION_GROUPS = 10;
export const MAX_OPTIONS_PER_GROUP = 30;
export const MAX_ZONES = 30;
const MAX_PRICE_MINOR = 100_000_000;
const MAX_FEE_MINOR = 1_000_000;
const MAX_STOCK = 1_000_000;

// photo: one of the listing's photos, shown when a buyer picks the option.
export type ListingOption = { name: string; priceDeltaMinor: number; stock: number | null; photo: string | null };
// kind: "option" (buyers pick exactly one; makes the variants) or "extra"
// (an add-on with an extra price). Missing on groups saved before kinds.
export type OptionGroup = { name: string; required: boolean; multi: boolean; kind?: 'option' | 'extra'; options: ListingOption[] };
export type DeliveryZone = { name: string; feeMinor: number };
export type ListingDelivery = { style: DeliveryStyle; zones: DeliveryZone[]; rules: DeliveryRule[]; flatMinor: number | null; pickup: boolean };

// Rent (per month or year, enquire only) and short stays (per night, booked
// on dates). `area` is the public area of the place, never the address.
export type PropertyDetails = {
  area: string;
  bedrooms: number | null;
  furnished: boolean;
  lister: 'owner' | 'agent';
  rentPeriod: 'month' | 'year' | null;
  advanceMonths: number | null;
  maxGuests: number | null;
  checkIn: string | null;
  checkOut: string | null;
  minNights: number | null;
};
export const MAX_STAY_NIGHTS = 30;
const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/;

export type ListingInput = {
  type: ListingType;
  name: string;
  // With priceByOption, the cheapest choice of the first option group; each
  // choice's priceDeltaMinor is what it costs above that.
  priceMinor: number;
  priceByOption: boolean;
  oldPriceMinor: number | null;
  // When the sale ends, the price goes back to oldPriceMinor (ISO time).
  saleEndsAt: string | null;
  // What can be sold in all (see effectiveStock); null: nothing caps it
  // (stored as `stockUnlimited`, never as a number).
  stock: number | null;
  // The seller's optional limit over every version ("30 plates a day"); it
  // goes down with each sale.
  stockLimit: number | null;
  category: string;
  description: string;
  photos: string[];
  condition: string;
  prepMinutes: number | null;
  madeToOrder: boolean;
  serviceMinutes: number | null;
  serviceWhere: 'my_place' | 'your_place' | 'either' | null;
  negotiable: boolean;
  optionGroups: OptionGroup[];
  // Null: not converted yet, the per-choice stock rules apply (variants.ts).
  variantRows: VariantRow[] | null;
  delivery: ListingDelivery;
  property: PropertyDetails | null;
  // An offer that applies by itself (deals.ts).
  deal: Deal | null;
  // The listing hides itself at this time (limited listings, flash drops).
  expiresAt: string | null;
  // The town tag (places.ts): suggested from where the seller is, changeable
  // or removable. Never a point.
  place: Place | null;
  active: boolean;
};

export type ListingParse = { ok: true; value: ListingInput } | { ok: false; field: string; message: string };

function text(value: unknown, max: number): string {
  return String(value ?? '').replace(/[ \t]+/g, ' ').trim().slice(0, max);
}

function wholeNumber(value: unknown, min: number, max: number): number | null {
  const number = Number(value);
  return Number.isInteger(number) && number >= min && number <= max ? number : null;
}

function fail(field: string, message: string): ListingParse {
  return { ok: false, field, message };
}

export function parseListingInput(raw: unknown, { isAllowedPhoto = (url: string) => url.startsWith('https://') } = {}): ListingParse {
  const input = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const type = LISTING_TYPES.includes(input.type as ListingType) ? input.type as ListingType : null;
  if (!type) return fail('type', 'Choose what kind of listing this is.');
  const name = text(input.name, 120);
  if (name.length < 2) return fail('name', 'Give your listing a name.');
  const priceMinor = wholeNumber(input.priceMinor, 1, MAX_PRICE_MINOR);
  if (priceMinor === null) return fail('price', 'Enter a price above zero.');
  const oldPriceMinor = input.oldPriceMinor === null || input.oldPriceMinor === undefined || input.oldPriceMinor === '' ? null : wholeNumber(input.oldPriceMinor, 1, MAX_PRICE_MINOR);
  if (oldPriceMinor !== null && oldPriceMinor <= priceMinor) return fail('oldPrice', 'The old price must be higher than the price.');
  const saleText = String(input.saleEndsAt ?? '').trim();
  const saleTime = saleText ? Date.parse(saleText) : Number.NaN;
  if (saleText && !Number.isFinite(saleTime)) return fail('saleEndsAt', 'Choose when the sale ends.');
  if (saleText && oldPriceMinor === null) return fail('saleEndsAt', 'Add the old price (“Was”) to set when the sale ends.');
  const saleEndsAt = saleText ? new Date(saleTime).toISOString() : null;
  // With rows the stock comes from them and the limit; listings saved
  // without rows (the live app, older forms) still send one number.
  const sentRows = input.variantRows !== undefined && input.variantRows !== null;
  const stock = sentRows ? 0 : wholeNumber(input.stock ?? (type === 'service' ? 1 : 0), 0, MAX_STOCK);
  if (stock === null) return fail('stock', 'Enter how many you have, as a whole number.');
  const limitSent = input.stockLimit !== undefined && input.stockLimit !== null && input.stockLimit !== '';
  const sentLimit = limitSent ? wholeNumber(input.stockLimit, 0, MAX_STOCK) : null;
  if (limitSent && sentLimit === null) return fail('stockLimit', 'Enter the limit as a whole number, or leave it empty.');
  const photos = Array.isArray(input.photos) ? input.photos.map(String).filter(Boolean) : [];
  if (!photos.length) return fail('photos', 'Add at least one photo.');
  if (photos.length > MAX_PHOTOS) return fail('photos', `Use up to ${MAX_PHOTOS} photos.`);
  if (photos.some(url => url.length > 2048 || !isAllowedPhoto(url))) return fail('photos', 'One of the photos could not be used. Upload it again.');

  const groupsInput = Array.isArray(input.optionGroups) ? input.optionGroups : [];
  if (groupsInput.length > MAX_OPTION_GROUPS) return fail('options', `Use up to ${MAX_OPTION_GROUPS} option groups.`);
  const optionGroups: OptionGroup[] = [];
  const groupNames = new Set<string>();
  for (const rawGroup of groupsInput) {
    const group = (rawGroup ?? {}) as Record<string, unknown>;
    const groupName = text(group.name, 60);
    if (!groupName) return fail('options', 'Name each option group, for example “Size”.');
    if (groupNames.has(groupName.toLowerCase())) return fail('options', `There are two groups called “${groupName}”.`);
    groupNames.add(groupName.toLowerCase());
    const optionsInput = Array.isArray(group.options) ? group.options : [];
    if (!optionsInput.length || optionsInput.length > MAX_OPTIONS_PER_GROUP) return fail('options', `Give “${groupName}” between 1 and ${MAX_OPTIONS_PER_GROUP} options.`);
    const optionNames = new Set<string>();
    const options: ListingOption[] = [];
    for (const rawOption of optionsInput) {
      const option = (rawOption ?? {}) as Record<string, unknown>;
      const optionName = text(option.name, 60);
      if (!optionName) return fail('options', `Name every option in “${groupName}”.`);
      if (optionNames.has(optionName.toLowerCase())) return fail('options', `“${optionName}” appears twice in “${groupName}”.`);
      optionNames.add(optionName.toLowerCase());
      const priceDeltaMinor = wholeNumber(option.priceDeltaMinor ?? 0, 0, MAX_PRICE_MINOR);
      if (priceDeltaMinor === null) return fail('options', `Check the extra price for “${optionName}”.`);
      const optionStock = option.stock === null || option.stock === undefined || option.stock === '' ? null : wholeNumber(option.stock, 0, MAX_STOCK);
      if (option.stock !== null && option.stock !== undefined && option.stock !== '' && optionStock === null) return fail('options', `Check the stock for “${optionName}”.`);
      const photo = option.photo ? String(option.photo) : null;
      if (photo !== null && !photos.includes(photo)) return fail('options', `Choose the photo for “${optionName}” again.`);
      options.push({ name: optionName, priceDeltaMinor, stock: optionStock, photo });
    }
    // An option is always one required choice; an extra is what the seller
    // set, except "pick one, must pick", which is an option.
    const pickOne = group.required === true && group.multi !== true;
    const kind = group.kind === 'option' || pickOne ? 'option' : 'extra';
    optionGroups.push(kind === 'option'
      ? { name: groupName, required: true, multi: false, kind, options }
      : { name: groupName, required: group.required === true, multi: group.multi === true, kind, options });
  }
  // Rows sent by the form; without them, rows from the per-choice stock where
  // that is exact. When every row has a stock, the listing's is their total.
  let variantRows: VariantRow[] | null;
  let stockLimit = sentLimit;
  if (sentRows) {
    const rows = parseVariantRows(input.variantRows, optionGroups, photos);
    if (!rows.ok) return fail('variants', rows.message);
    variantRows = rows.value;
  } else {
    variantRows = rowsFromChoiceStock(optionGroups, priceMinor);
    // The one number sent caps rows without a stock of their own.
    if (variantRows && !limitSent && variantRows.some(row => row.stock === null)) stockLimit = stock;
  }
  const stockTotal = variantRows ? effectiveStock(variantRows, stockLimit) : stock;
  const priceByOption = input.priceByOption === true;
  if (priceByOption) {
    const first = optionGroups[0];
    if (!first || !first.required || first.multi) return fail('options', 'Add the choices that set the price, for example Small, Medium and Large.');
    if (first.options.length < 2) return fail('options', `Give “${first.name}” at least two choices, or set one price instead.`);
    if (!first.options.some(option => option.priceDeltaMinor === 0)) return fail('options', `Check the prices in “${first.name}”.`);
  }

  let property: PropertyDetails | null = null;
  if (PROPERTY_TYPES.has(type)) {
    const raw = (input.property && typeof input.property === 'object' ? input.property : {}) as Record<string, unknown>;
    const area = text(raw.area, 80);
    if (area.length < 2) return fail('property', 'Name the area the place is in, for example Asokwa, Kumasi.');
    if (/\d[\d\s().-]{5,}\d/.test(area)) return fail('property', 'The area is public, so it cannot include a phone number.');
    const bedrooms = raw.bedrooms === null || raw.bedrooms === undefined || raw.bedrooms === '' ? null : wholeNumber(raw.bedrooms, 0, 50);
    property = {
      area, bedrooms, furnished: raw.furnished === true, lister: raw.lister === 'agent' ? 'agent' : 'owner',
      rentPeriod: null, advanceMonths: null, maxGuests: null, checkIn: null, checkOut: null, minNights: null,
    };
    if (type === 'rent') {
      if (raw.rentPeriod !== 'month' && raw.rentPeriod !== 'year') return fail('property', 'Choose per month or per year.');
      property.rentPeriod = raw.rentPeriod;
      property.advanceMonths = raw.advanceMonths === null || raw.advanceMonths === undefined || raw.advanceMonths === '' ? null : wholeNumber(raw.advanceMonths, 0, 24);
    } else {
      property.maxGuests = wholeNumber(raw.maxGuests, 1, 50);
      if (property.maxGuests === null) return fail('property', 'Say how many guests can stay.');
      property.checkIn = CLOCK.test(String(raw.checkIn)) ? String(raw.checkIn) : '14:00';
      property.checkOut = CLOCK.test(String(raw.checkOut)) ? String(raw.checkOut) : '11:00';
      property.minNights = wholeNumber(raw.minNights ?? 1, 1, MAX_STAY_NIGHTS);
      if (property.minNights === null) return fail('property', `Use a minimum stay of 1 to ${MAX_STAY_NIGHTS} nights.`);
    }
  }

  const deal = parseDeal(input.deal);
  if (!deal.ok) return fail('deal', deal.message);
  if (deal.value && ENQUIRE_ONLY.has(type)) return fail('deal', 'Deals are for listings bought through checkout.');
  const expiresText = String(input.expiresAt ?? '').trim();
  const expires = expiresText ? Date.parse(expiresText) : Number.NaN;
  if (expiresText && !Number.isFinite(expires)) return fail('expiresAt', 'Choose when the listing comes down.');

  // Places are not delivered: rent is enquire-only and a stay is checked into.
  const deliveryInput = (PROPERTY_TYPES.has(type) ? { style: 'pickup_only' } : input.delivery && typeof input.delivery === 'object' ? input.delivery : {}) as Record<string, unknown>;
  const style = DELIVERY_STYLES.includes(deliveryInput.style as DeliveryStyle) ? deliveryInput.style as DeliveryStyle : null;
  if (!style) return fail('delivery', 'Choose how buyers get this.');
  const zonesInput = Array.isArray(deliveryInput.zones) ? deliveryInput.zones : [];

  const zones: DeliveryZone[] = [];
  if (style === 'zones') {
    if (!zonesInput.length || zonesInput.length > MAX_ZONES) return fail('delivery', `Add between 1 and ${MAX_ZONES} delivery areas.`);
    for (const rawZone of zonesInput) {
      const zone = (rawZone ?? {}) as Record<string, unknown>;
      const zoneName = text(zone.name, 80);
      const feeMinor = wholeNumber(zone.feeMinor, 0, MAX_FEE_MINOR);
      if (!zoneName || feeMinor === null) return fail('delivery', 'Give every delivery area a name and a fee.');
      zones.push({ name: zoneName, feeMinor });
    }
  }
  let rules: DeliveryRule[] = [];
  if (style === 'rules') {
    const parsedRules = parseDeliveryRules(deliveryInput.rules);
    if (!parsedRules.ok) return fail('delivery', parsedRules.message);
    rules = parsedRules.value;
  }
  const flatMinor = style === 'flat' ? wholeNumber(deliveryInput.flatMinor, 0, MAX_FEE_MINOR) : null;
  if (style === 'flat' && flatMinor === null) return fail('delivery', 'Enter the delivery fee.');

  return {
    ok: true,
    value: {
      type, name, priceMinor, priceByOption, oldPriceMinor, saleEndsAt, stock: stockTotal, stockLimit: variantRows ? stockLimit : null,
      category: text(input.category, 60),
      description: String(input.description ?? '').trim().slice(0, 2000),
      photos,
      condition: type === 'item' || type === 'big' ? text(input.condition, 40) : '',
      prepMinutes: type === 'food' ? wholeNumber(input.prepMinutes, 0, 24 * 60) : null,
      madeToOrder: type === 'food' && input.madeToOrder === true,
      serviceMinutes: type === 'service' ? wholeNumber(input.serviceMinutes, 5, 24 * 60) : null,
      serviceWhere: type === 'service' && ['my_place', 'your_place', 'either'].includes(String(input.serviceWhere)) ? input.serviceWhere as ListingInput['serviceWhere'] : null,
      negotiable: type === 'big' && input.negotiable === true,
      optionGroups,
      variantRows,
      delivery: { style, zones, rules, flatMinor, pickup: !PROPERTY_TYPES.has(type) && (style === 'pickup_only' || deliveryInput.pickup === true) },
      property,
      deal: deal.value,
      expiresAt: expiresText ? new Date(expires).toISOString() : null,
      place: parsePlace(input.place),
      active: input.active !== false,
    },
  };
}

function slug(value: string, index: number): string {
  return `${value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'option'}-${index + 1}`;
}

// The fields the live app's market, cart and checkout read.
export function legacyProductFields(listing: ListingInput) {
  const style = listing.delivery.style;
  return {
    name: listing.name,
    title: listing.name,
    price: listing.priceMinor / 100,
    oldPrice: listing.oldPriceMinor === null ? null : listing.oldPriceMinor / 100,
    saleEndsAt: listing.saleEndsAt,
    // The Worker's cron finds timed sales by this flag and ends them.
    onTimedSale: listing.saleEndsAt !== null,
    // The live app only knows a number: a placeholder when nothing caps the
    // listing. The app and the Worker go by `stockUnlimited`.
    quantity: listing.stock ?? LEGACY_UNLIMITED_QUANTITY,
    stockUnlimited: listing.stock === null,
    status: !listing.active ? 'Inactive' : listing.stock === 0 ? 'Out of Stock' : 'Active',
    category: listing.category,
    description: listing.description,
    condition: listing.condition,
    images: listing.photos,
    img: listing.photos[0],
    currency: 'GHS',
    // The live app's option groups, with stock and extras kept in step with the rows.
    variants: legacyVariantStock(listing.optionGroups, listing.variantRows, listing.priceMinor).map((group, groupIndex) => ({
      id: slug(group.name, groupIndex), name: group.name, required: group.required, multi: group.multi, ...(group.kind ? { kind: group.kind } : {}),
      opts: group.options.map((option, optionIndex) => ({ id: slug(option.name, optionIndex), name: option.name, extra: option.priceDeltaMinor / 100, stock: option.stock })),
    })),
    // Null removes old rows: the listing is back on per-choice stock.
    variantRows: listing.variantRows,
    stockLimit: listing.stockLimit,
    optionPhotos: optionPhotoMap(listing.optionGroups),
    imageVariantLinks: legacyImageVariantLinks(listing.optionGroups, listing.photos),
    // The old app has no map pricing, so it offers those listings as pickup only.
    deliveryMode: style === 'zones' ? 'per' : style === 'flat' ? 'flat' : style === 'free' ? 'free' : 'desc',
    destinations: listing.delivery.zones.map(zone => ({ name: zone.name, fee: zone.feeMinor / 100 })),
    flatRate: style === 'flat' ? (listing.delivery.flatMinor ?? 0) / 100 : null,
    freeDelivery: style === 'free',
    allowPickup: listing.delivery.pickup,
    deliveryDescription: style === 'pickup_only' ? 'Pickup only' : style === 'rules' ? 'Delivery areas on Guildite' : '',
    // Rent and stays: the live app cannot sell them (no pickup, no delivery fee).
    ...(PROPERTY_TYPES.has(listing.type) ? { allowPickup: false, deliveryDescription: listing.type === 'rent' ? 'For rent: enquire on Guildite' : 'Short stay: book on Guildite' } : {}),
  };
}

export type ListingView = ListingInput & {
  id: string;
  shopId: string;
  sellerId: string;
  shopName: string;
  shopHandle: string | null;
  shopArea: string;
  // The shop's public point (exact pin, or the area point for home sellers).
  shopPoint: { lat: number; lng: number } | null;
  status: 'Active' | 'Inactive' | 'Out of Stock';
  createdAt: string | null;
  views: number;
  saves: number;
  // Up to 60 s, verified by the Worker with Cloudinary (legacy `video` field).
  // Its cover is a frame or photo the seller chose (posterUrl), never the first photo.
  video?: { url: string; posterUrl?: string | null; publicId?: string } | null;
  // Nothing caps the listing (stock is null): no limit, and a version
  // without a number of its own.
  stockUnlimited: boolean;
};

// The price a product sells at now: a timed sale that has ended is back at
// its old price, even before the Worker's cron rewrites the record.
export function saleHasEnded(product: Record<string, unknown>, now = Date.now()): boolean {
  const ends = Date.parse(String(product.saleEndsAt ?? ''));
  return Number.isFinite(ends) && ends <= now && Number(product.oldPrice) > 0;
}

// Reads both v2 listings and old products into one shape for the app.
export function listingFromProduct(id: string, product: Record<string, unknown>, now = Date.now()): ListingView {
  const ended = saleHasEnded(product, now);
  const expiresAt = typeof product.expiresAt === 'string' ? product.expiresAt : null;
  const expired = expiresAt !== null && Date.parse(expiresAt) <= now;
  const deal = parseDeal(product.deal);
  const toMinor = (value: unknown) => (Number.isFinite(Number(value)) ? Math.round(Number(value) * 100) : 0);
  const variants = Array.isArray(product.variants) ? product.variants as Record<string, unknown>[] : [];
  const destinations = Array.isArray(product.destinations) ? product.destinations as Record<string, unknown>[] : [];
  const mode = String(product.deliveryMode || '');
  const v2Delivery = (product.deliveryV2 && typeof product.deliveryV2 === 'object' ? product.deliveryV2 : {}) as Record<string, unknown>;
  const rules = readDeliveryRules(v2Delivery.rules);
  const lat = Number(product.sellerLat);
  const lng = Number(product.sellerLng);
  const style: DeliveryStyle = v2Delivery.style === 'rules' && rules.length ? 'rules' : product.freeDelivery === true || mode === 'free' ? 'free'
    : mode === 'flat' ? 'flat' : mode === 'per' && destinations.length ? 'zones' : 'pickup_only';
  const photos = (Array.isArray(product.images) ? product.images : [product.img]).map(String).filter(url => url.startsWith('https://') || url.startsWith('http://127.0.0.1'));
  const photoOf = readOptionPhotos(product, photos);
  const optionGroups: OptionGroup[] = variants.map(group => ({
    name: String(group.name || ''), required: group.required === true, multi: group.multi === true,
    ...(group.kind === 'option' || group.kind === 'extra' ? { kind: group.kind } : {}),
    options: (Array.isArray(group.opts) ? group.opts as Record<string, unknown>[] : []).map(option => ({
      name: String(option.name || ''), priceDeltaMinor: toMinor(option.extra), stock: option.stock === null || option.stock === undefined ? null : Number(option.stock),
      photo: photoOf(String(group.name || ''), String(option.name || '')),
    })),
  }));
  const status = ['Active', 'Inactive', 'Out of Stock'].includes(String(product.status)) ? product.status as ListingView['status'] : 'Active';
  const rows = readVariantRows(product.variantRows, optionGroups);
  const quantity = Number.isInteger(Number(product.quantity)) ? Number(product.quantity) : 0;
  // Rows saved before the limit had its own field used the stock as the cap.
  const stockLimit = typeof product.stockLimit === 'number' ? product.stockLimit
    : product.stockLimit === null ? null : rows?.some(row => row.stock === null) ? quantity : null;
  return {
    id,
    shopId: String(product.shopId || product.sellerId || ''),
    sellerId: String(product.sellerId || product.sellerUid || ''),
    shopName: String(product.shopName || product.seller || 'Shop'),
    shopHandle: typeof product.shopHandle === 'string' ? product.shopHandle : null,
    shopArea: [product.neighbourhood, product.sellerCity].filter(Boolean).join(', '),
    shopPoint: product.sellerLat != null && product.sellerLng != null && Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0) ? { lat, lng } : null,
    type: LISTING_TYPES.includes(product.listingType as ListingType) ? product.listingType as ListingType : 'item',
    name: String(product.name || product.title || 'Listing'),
    priceMinor: toMinor(ended ? product.oldPrice : product.price),
    saleEndsAt: !ended && typeof product.saleEndsAt === 'string' && product.oldPrice ? product.saleEndsAt : null,
    priceByOption: product.priceByOption === true && variants.length > 0,
    oldPriceMinor: product.oldPrice && !ended ? toMinor(product.oldPrice) : null,
    stock: product.stockUnlimited === true ? null : quantity,
    category: String(product.category || ''),
    description: String(product.description || ''),
    photos,
    condition: String(product.condition || ''),
    prepMinutes: Number.isFinite(Number(product.prepMinutes)) ? Number(product.prepMinutes) : null,
    madeToOrder: product.madeToOrder === true,
    serviceMinutes: Number.isFinite(Number(product.serviceMinutes)) ? Number(product.serviceMinutes) : null,
    serviceWhere: (['my_place', 'your_place', 'either'].includes(String(product.serviceWhere)) ? product.serviceWhere : null) as ListingView['serviceWhere'],
    negotiable: product.negotiable === true,
    optionGroups,
    variantRows: rows,
    stockLimit,
    stockUnlimited: product.stockUnlimited === true,
    delivery: {
      style,
      zones: destinations.map(zone => ({ name: String(zone.name || ''), feeMinor: toMinor(zone.fee) })),
      rules,
      flatMinor: mode === 'flat' ? toMinor(product.flatRate) : null,
      pickup: product.allowPickup === true,
    },
    property: readProperty(product.property),
    deal: deal.ok ? deal.value : null,
    expiresAt,
    place: parsePlace(product.place),
    active: status !== 'Inactive' && !expired,
    status: expired ? 'Inactive' : status,
    createdAt: typeof product.createdAt === 'string' ? product.createdAt : null,
    views: Number(product.views || 0),
    saves: Number(product.wishlistCount || 0),
    video: listingVideo(product.video),
  };
}

function readProperty(value: unknown): PropertyDetails | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const count = (item: unknown) => (Number.isInteger(item) ? Number(item) : null);
  return {
    area: String(raw.area || ''), bedrooms: count(raw.bedrooms), furnished: raw.furnished === true, lister: raw.lister === 'agent' ? 'agent' : 'owner',
    rentPeriod: raw.rentPeriod === 'month' || raw.rentPeriod === 'year' ? raw.rentPeriod : null, advanceMonths: count(raw.advanceMonths),
    maxGuests: count(raw.maxGuests), checkIn: typeof raw.checkIn === 'string' ? raw.checkIn : null, checkOut: typeof raw.checkOut === 'string' ? raw.checkOut : null,
    minNights: count(raw.minNights),
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

// The nights of a stay: check-in date up to, not including, check-out.
export function stayNights(from: string, to: string): string[] {
  if (!DATE.test(from) || !DATE.test(to)) return [];
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  const nights: string[] = [];
  for (let at = start; at < end && nights.length <= MAX_STAY_NIGHTS; at += DAY_MS) nights.push(new Date(at).toISOString().slice(0, 10));
  return nights;
}

export type StayRequest = { from: string; to: string; guests: number };
export type StayCheck = { ok: true; nights: string[] } | { ok: false; message: string };

// A booking request against the place's rules and the nights already taken.
// `today` is the date where the place is (YYYY-MM-DD).
export function checkStay(property: PropertyDetails | null, request: StayRequest, today: string, booked: ReadonlySet<string> = new Set()): StayCheck {
  if (!property?.maxGuests) return { ok: false, message: 'This place cannot be booked.' };
  const nights = stayNights(request.from, request.to);
  if (!nights.length) return { ok: false, message: 'Choose your check-in and check-out dates.' };
  if (request.from < today) return { ok: false, message: 'Check-in cannot be in the past.' };
  if (Date.parse(`${request.from}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`) > 365 * DAY_MS) return { ok: false, message: 'Book up to a year ahead.' };
  if (nights.length > MAX_STAY_NIGHTS) return { ok: false, message: `Book up to ${MAX_STAY_NIGHTS} nights at a time.` };
  if (nights.length < (property.minNights ?? 1)) return { ok: false, message: `The minimum stay is ${property.minNights} nights.` };
  if (!Number.isInteger(request.guests) || request.guests < 1 || request.guests > property.maxGuests) return { ok: false, message: `Up to ${property.maxGuests} guests can stay.` };
  const taken = nights.find(night => booked.has(night));
  if (taken) return { ok: false, message: `The night of ${taken} is already booked. Choose other dates.` };
  return { ok: true, nights };
}

function listingVideo(value: unknown): { url: string; posterUrl: string | null; publicId?: string } | null {
  const data = (value ?? {}) as { url?: unknown; posterUrl?: unknown; publicId?: unknown };
  const url = String(data.url || '');
  const ours = (address: string) => /^https:\/\/res\.cloudinary\.com\/[^/]+\/(image|video)\/upload\//.test(address) || /^http:\/\/127\.0\.0\.1:\d+\/media\//.test(address);
  if (!/^https:\/\/res\.cloudinary\.com\/[^/]+\/video\/upload\//.test(url) && !/^http:\/\/127\.0\.0\.1:\d+\/media\//.test(url)) return null;
  const poster = String(data.posterUrl || '');
  const publicId = typeof data.publicId === 'string' && data.publicId ? data.publicId : undefined;
  return { url, posterUrl: ours(poster) ? poster : null, ...(publicId ? { publicId } : {}) };
}

export type OptionSelection = Record<string, string[]>;

// variantId: the row picked (null on a listing not converted yet).
export type SelectionCheck = { ok: true; unitMinor: number; summary: string; variantId: string | null } | { ok: false; message: string };

type Selectable = Pick<ListingInput, 'priceMinor' | 'optionGroups'> & Partial<Pick<ListingInput, 'variantRows' | 'stock'>>;

// What a buyer picked: required groups chosen, one pick unless multi, and a
// variant with enough stock (or, before conversion, no sold-out choices),
// within the listing's own stock.
// The variant sets the price; add-ons add theirs. The server repeats this
// check at checkout.
export function checkSelection(listing: Selectable, selection: OptionSelection, quantity = 1): SelectionCheck {
  const rows = listing.variantRows ?? null;
  const vary = rows ? variantGroups(listing.optionGroups) : [];
  let unitMinor = listing.priceMinor;
  const parts: string[] = [];
  if (listing.stock !== undefined && listing.stock !== null && listing.stock < quantity) return { ok: false, message: listing.stock > 0 ? `Only ${listing.stock} left.` : 'This listing is sold out.' };
  for (const group of listing.optionGroups) {
    const picked = selection[group.name] ?? [];
    if (group.required && !picked.length) return { ok: false, message: `Choose a ${group.name}.` };
    if (!group.multi && picked.length > 1) return { ok: false, message: `Choose one ${group.name}.` };
    for (const name of picked) {
      const option = group.options.find(item => item.name === name);
      if (!option) return { ok: false, message: `${name} is no longer available.` };
      if (vary.includes(group)) continue;
      if (option.stock !== null && option.stock < quantity) return { ok: false, message: `${name} is sold out.` };
      unitMinor += option.priceDeltaMinor;
    }
    if (picked.length) parts.push(`${group.name}: ${picked.join(', ')}`);
  }
  if (!rows) return { ok: true, unitMinor, summary: parts.join(' · '), variantId: null };
  const row = findVariant(rows, listing.optionGroups, group => selection[group]?.[0]);
  if (!row) return { ok: false, message: 'That combination is no longer available.' };
  const label = variantLabel(row.options, vary);
  if (!rowHasStock(row)) return { ok: false, message: `${label || 'This listing'} is sold out.` };
  if (!rowHasStock(row, quantity)) return { ok: false, message: `Only ${row.stock} left${label ? ` in ${label}` : ''}.` };
  return { ok: true, unitMinor: unitMinor + variantPrice(row, listing.priceMinor) - listing.priceMinor, summary: parts.join(' · '), variantId: row.id };
}

// A listing that sells on per-choice stock and needs its seller to set stock
// for each combination (DECISIONS, 23 October).
export function needsVariantStock(listing: Pick<ListingInput, 'optionGroups' | 'variantRows'>): boolean {
  return listing.variantRows === null && variantGroups(listing.optionGroups).length > 0;
}

// The selection in the legacy checkout shape: { Size: [{ opt: 'Large' }] }.
export function legacySelections(selection: OptionSelection): Record<string, { opt: string }[]> {
  return Object.fromEntries(Object.entries(selection).filter(([, names]) => names.length).map(([group, names]) => [group, names.map(opt => ({ opt }))]));
}

export type PriceRange = { minMinor: number; maxMinor: number; extras: boolean };

// What one unit can cost: the cheapest and dearest variant (or, before
// conversion, required groups' cheapest and dearest choice), plus required
// add-ons; optional choices that cost more count as extras.
export function priceRange(listing: Selectable): PriceRange {
  const rows = listing.variantRows ?? null;
  const prices = rows?.map(row => variantPrice(row, listing.priceMinor)) ?? [];
  let minMinor = prices.length ? Math.min(...prices) : listing.priceMinor;
  let maxMinor = prices.length ? Math.max(...prices) : listing.priceMinor;
  let extras = false;
  const vary = rows ? variantGroups(listing.optionGroups) : [];
  for (const group of listing.optionGroups) {
    if (vary.includes(group)) continue;
    const deltas = group.options.map(option => option.priceDeltaMinor);
    if (!deltas.length) continue;
    if (group.required) {
      minMinor += Math.min(...deltas);
      maxMinor += Math.max(...deltas);
    } else if (deltas.some(delta => delta > 0)) {
      extras = true;
    }
  }
  return { minMinor, maxMinor, extras };
}

// " / month", " / year", " / night" for places; nothing for other listings.
export function priceUnit(listing: Partial<Pick<ListingInput, 'type' | 'property'>>): string {
  if (listing.type === 'stay') return ' / night';
  if (listing.type === 'rent') return listing.property?.rentPeriod === 'year' ? ' / year' : ' / month';
  return '';
}

// Cards and lists: "GHC 30", "From GHC 30" when choices change the price,
// "GHC 1,500 / month" for rent.
export function priceLabel(listing: Selectable & Partial<Pick<ListingInput, 'type' | 'property'>>): string {
  const range = priceRange(listing);
  const price = range.maxMinor > range.minMinor || range.extras ? `From ${formatMoney(range.minMinor)}` : formatMoney(range.minMinor);
  return `${price}${priceUnit(listing)}`;
}

// "Colour / Size: Red / Large GHC 60 (3 left), Blue / Large GHC 60 (no limit); Toppings:
// Cheese +GHC 5". Before conversion: "Size: Small GHC 30, Large GHC 50".
function optionsText(listing: ListingInput): string {
  const rows = listing.variantRows;
  const vary = rows ? variantGroups(listing.optionGroups) : [];
  const variants = rows && vary.length ? [`${vary.map(group => group.name).join(' / ')}: ` + rows.map(row => `${variantLabel(row.options, vary)} ${formatMoney(variantPrice(row, listing.priceMinor))} (${row.stock === null ? 'no limit' : `${row.stock} left`})`).join(', ')] : [];
  return [...variants, ...listing.optionGroups.filter(group => !vary.includes(group)).map((group, index) => `${group.name}: ${group.options.map(option => (
    !rows && listing.priceByOption && index === 0 ? `${option.name} ${formatMoney(listing.priceMinor + option.priceDeltaMinor)}`
      : `${option.name}${option.priceDeltaMinor ? ` +${formatMoney(option.priceDeltaMinor)}` : ''}`
  )).join(', ')}`)].join('; ');
}

function deliveryCsvText(delivery: ListingDelivery): string {
  const fee = (minor: number) => (minor ? formatMoney(minor) : 'free');
  const ways = {
    zones: delivery.zones.map(zone => `${zone.name} ${fee(zone.feeMinor)}`).join(', '),
    rules: delivery.rules.map(describeRule).join('; '),
    flat: `flat ${fee(delivery.flatMinor ?? 0)}`,
    free: 'free delivery',
    pickup_only: 'pickup only',
  }[delivery.style];
  return delivery.pickup && delivery.style !== 'pickup_only' ? `${ways}; pickup too` : ways;
}

// A shop's catalogue for a spreadsheet (Dashboard and Listings → Export).
export function catalogueCsv(listings: ListingView[], origin: string): string {
  return csvText([
    ['Listing', 'Type', 'Category', 'Price (GHS)', 'Was (GHS)', 'Sale ends', 'Deal', 'Comes down', 'In stock', 'Options', 'Delivery', 'Shown to buyers', 'Views', 'Saves', 'Description', 'Cover photo', 'Link'],
    ...listings.map(item => [
      item.name, LISTING_TYPE_LABELS[item.type], item.category,
      `${item.priceByOption ? 'from ' : ''}${(item.priceMinor / 100).toFixed(2)}`, item.oldPriceMinor === null ? '' : (item.oldPriceMinor / 100).toFixed(2),
      item.saleEndsAt ?? '', item.deal ? dealLabel(item.deal) : '', item.expiresAt ?? '', item.stock === null ? 'No limit' : String(item.stock), optionsText(item), deliveryCsvText(item.delivery),
      item.active ? 'Yes' : 'No', String(item.views || 0), String(item.saves || 0), item.description, item.photos[0] ?? '', `${origin}/p/${item.id}`,
    ]),
  ]);
}
